import { useEffect, useState } from 'react';
import { useBusinessNav } from '@/hooks/useBusinessNav';
import { useApp } from '@/components/AppLayout';
import { reportsApi, type ReportsSummary } from '@/lib/api';
import { formatAmount } from '@/lib/currency';
import { ListPage, ListPageHeader, ListPageTitle } from '@/components/ui/list-page';
import { cn } from '@/lib/utils';
import {
  ChevronRight,
  Clock,
  CreditCard,
  TrendingUp,
  FileText,
  Users,
  Receipt,
} from 'lucide-react';

interface StatCardProps {
  title: string;
  stat: string;
  statLabel: string;
  href: string;
  icon: React.ReactNode;
  iconBg: string;
  accentColor?: string;
}

function StatCard({ title, stat, statLabel, href, icon, iconBg, accentColor }: StatCardProps) {
  const { nav } = useBusinessNav();

  return (
    <button
      onClick={() => nav(href)}
      className="bg-card rounded-xl border shadow-sm p-5 text-left hover:shadow-md transition-all duration-200 group relative overflow-hidden"
    >
      <div className={cn('absolute top-0 right-0 w-32 h-32 rounded-full blur-3xl opacity-20 -translate-y-1/2 translate-x-1/2', accentColor)} />
      <div className="flex items-start justify-between mb-4 relative">
        <div className={cn('h-11 w-11 rounded-xl flex items-center justify-center', iconBg)}>
          {icon}
        </div>
        <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:translate-x-1 transition-transform" />
      </div>
      <div className="relative">
        <p className="text-sm font-medium text-muted-foreground mb-1">{title}</p>
        <p className="text-3xl font-semibold tracking-tight mb-1">{stat}</p>
        <p className="text-sm text-muted-foreground">{statLabel}</p>
      </div>
    </button>
  );
}

interface ReportLinkProps {
  title: string;
  description: string;
  href: string;
  icon: React.ReactNode;
  iconBg: string;
}

function ReportLink({ title, description, href, icon, iconBg }: ReportLinkProps) {
  const { nav } = useBusinessNav();

  return (
    <button
      onClick={() => nav(href)}
      className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-muted/50 transition-colors group border-b"
    >
      <div className={cn('h-10 w-10 rounded-xl flex items-center justify-center shrink-0', iconBg)}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">{title}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:translate-x-1 transition-transform shrink-0" />
    </button>
  );
}

export default function Reports() {
  const { currentBusiness } = useApp();
  const [summary, setSummary] = useState<ReportsSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (currentBusiness) {
      loadSummary();
    }
  }, [currentBusiness]);

  const loadSummary = async () => {
    if (!currentBusiness) return;
    setLoading(true);
    try {
      const response = await reportsApi.getSummary(currentBusiness.id);
      if (response.data) {
        setSummary(response.data);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <ListPage>
      <ListPageHeader>
        <ListPageTitle subtitle="Track your business performance">Reports</ListPageTitle>
        <div />
      </ListPageHeader>

      <div className="px-6 space-y-6">
        {/* Hero Stats */}
        {loading ? (
          <div className="grid gap-4 md:grid-cols-2">
            <div className="bg-card rounded-xl border shadow-sm p-5 animate-pulse">
              <div className="h-11 w-11 bg-muted rounded-xl mb-4" />
              <div className="h-4 bg-muted rounded w-32 mb-3" />
              <div className="h-9 bg-muted rounded w-40 mb-2" />
              <div className="h-4 bg-muted rounded w-24" />
            </div>
            <div className="bg-card rounded-xl border shadow-sm p-5 animate-pulse">
              <div className="h-11 w-11 bg-muted rounded-xl mb-4" />
              <div className="h-4 bg-muted rounded w-32 mb-3" />
              <div className="h-9 bg-muted rounded w-40 mb-2" />
              <div className="h-4 bg-muted rounded w-24" />
            </div>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            <StatCard
              title="Outstanding Invoices"
              stat={formatAmount(summary?.outstanding_invoices.total || '0')}
              statLabel={`${summary?.outstanding_invoices.count || 0} unpaid invoices`}
              href="/reports/outstanding-invoices"
              icon={<Clock className="h-5 w-5 text-amber-600" />}
              iconBg="bg-amber-100 dark:bg-amber-950"
              accentColor="bg-amber-500"
            />
            <StatCard
              title="Payments This Month"
              stat={formatAmount(summary?.payments_this_month.total || '0')}
              statLabel={`${summary?.payments_this_month.count || 0} payments received`}
              href="/reports/payments"
              icon={<CreditCard className="h-5 w-5 text-emerald-600" />}
              iconBg="bg-emerald-100 dark:bg-emerald-950"
              accentColor="bg-emerald-500"
            />
          </div>
        )}

        {/* All Reports */}
        <div className="bg-card rounded-xl border shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b bg-muted/30">
            <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              All Reports
            </h2>
          </div>
          <div>
            <ReportLink
              title="Revenue Summary"
              description="Revenue breakdown by week, month, or custom period"
              href="/reports/revenue"
              icon={<TrendingUp className="h-5 w-5 text-blue-600" />}
              iconBg="bg-blue-100 dark:bg-blue-950"
            />
            <ReportLink
              title="Invoice History"
              description="All invoices with status filters and date ranges"
              href="/reports/invoices"
              icon={<FileText className="h-5 w-5 text-violet-600" />}
              iconBg="bg-violet-100 dark:bg-violet-950"
            />
            <ReportLink
              title="Sales by Client"
              description="Top clients ranked by total revenue"
              href="/reports/sales-by-client"
              icon={<Users className="h-5 w-5 text-pink-600" />}
              iconBg="bg-pink-100 dark:bg-pink-950"
            />
            <ReportLink
              title="Sales Tax Summary"
              description="Tax collected by rate and category"
              href="/reports/sales-tax"
              icon={<Receipt className="h-5 w-5 text-cyan-600" />}
              iconBg="bg-cyan-100 dark:bg-cyan-950"
            />
          </div>
        </div>
      </div>
    </ListPage>
  );
}
